<template>
  <div class="tw-flex tw-flex-col tw-gap-4">
    <info-banner-index />
    <div class="tw-flex tw-flex-row tw-gap-10">
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        <div>
          <span class="tw-text-sm">{{ t("light-mode-logo") }}</span>

          <authorisation-wrapper
            :resource="ADMINISTRATORS"
            :action="WRITE"
            v-slot="{ isAllowed: isWriteAllowed }"
          >
            <drop-zone-index
              :data-test-id="`msp-branding-logo-light-image-drop-zone`"
              @file-added="handleLogoUpload"
              :disabled="
                (lightLogo.display !== null && lightLogo.isUploadedUsed) ||
                isLoading ||
                !isWriteAllowed ||
                (authUserRole === 'admin' && authUserLevel === 'msp')
              "
              mode="light"
            />
          </authorisation-wrapper>
        </div>
        <template
          v-if="
            lightLogo.display &&
            lightLogo.display.src &&
            lightLogo.display.name !== DEFAULT_BRANDING_LIGHT_LOGO_NAME
          "
        >
          <authorisation-wrapper
            :resource="ADMINISTRATORS"
            :action="WRITE"
            v-slot="{ isAllowed: isWriteAllowed }"
          >
            <file-preview-index
              :file-name="lightLogo.display.name"
              :file-size="lightLogo.display.size"
              :src="appendFullImageUrl(lightLogo.display.url)"
              mode="light"
              @remove-file="handleLogoRemoval"
              :readonly="
                !isWriteAllowed ||
                (authUserRole === 'admin' && authUserLevel === 'msp')
              "
            />
          </authorisation-wrapper>
        </template>
      </div>
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        <div>
          <span class="tw-text-sm">{{ t("dark-mode-logo") }}</span>

          <authorisation-wrapper
            :resource="ADMINISTRATORS"
            :action="WRITE"
            v-slot="{ isAllowed: isWriteAllowed }"
          >
            <drop-zone-index
              :data-test-id="`msp-branding-logo-dark-image-drop-zone`"
              @file-added="handleLogoUpload"
              :disabled="
                (darkLogo.display !== null && darkLogo.isUploadedUsed) ||
                isLoading ||
                !isWriteAllowed ||
                (authUserRole === 'admin' && authUserLevel === 'msp')
              "
              mode="dark"
            />
          </authorisation-wrapper>
        </div>
        <template v-if="darkLogo.display && darkLogo.display.src">
          <authorisation-wrapper
            :resource="ADMINISTRATORS"
            :action="WRITE"
            v-slot="{ isAllowed: isWriteAllowed }"
          >
            <file-preview-index
              :file-name="darkLogo.display.name"
              :file-size="darkLogo.display.size"
              :src="appendFullImageUrl(darkLogo.display.url)"
              mode="dark"
              @remove-file="handleLogoRemoval"
              :readonly="
                !isWriteAllowed ||
                (authUserRole === 'admin' && authUserLevel === 'msp')
              "
            />
          </authorisation-wrapper>
        </template>
      </div>
    </div>
  </div>

  <t-delete-confirm
    :visible="isDeleteModalVisible"
    :loading="isLoading"
    @on-close="isDeleteModalVisible = false"
    @on-confirm="confirmLogoRemoval"
  >
    <template #title>
      <cl-heading :size="'h4'">
        {{ $t("delete-logo") }}
      </cl-heading>
    </template>

    <template #body>
      <div class="tw-relative">
        <cl-p>
          {{ deleteModalMessage }}
        </cl-p>
      </div>
    </template>
  </t-delete-confirm>
</template>

<script lang="ts">
interface ImageDetails {
  name: string;
  size: number;
  type: string;
  src: string;
  url: string;
}

interface LogoData {
  applied: ImageDetails | null;
  uploaded: ImageDetails | null;
  display: ImageDetails | null;
  isUploadedUsed: boolean;
}

type DeleteModalOption = "delete-uploaded" | "delete-applied" | "delete-last";
</script>
<script setup lang="ts">
import FilePreviewIndex from "./file-preview/preview.index.vue";
import DropZoneIndex from "./drop-zone/drop-zone.index.vue";
import { ref, computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthenticationStore } from "@/store/authentication.store";
import { storeToRefs } from "pinia";
import { useBrandingStore } from "@/store/branding.store";
import { DEFAULT_BRANDING_LIGHT_LOGO_NAME } from "@/modules/Settings/constants";
import TDeleteConfirm from "@/modals/TDeleteConfirm.vue";
import { ADMINISTRATORS, AuthorisationWrapper, WRITE } from "@/authorisation";
import { extractFilename, blobToBase64, appendFullImageUrl } from "./helpers";
import InfoBannerIndex from "./info-banner/info-banner.index.vue";

const { t } = useI18n();
const authStore = useAuthenticationStore();
const brandingStore = useBrandingStore();

const { organisationBranding: branding, uploadedLogos } =
  storeToRefs(brandingStore);

const selectedMsp = computed(() => authStore.selectedMsp);
const selectedAccount = computed(() => authStore.selectedAccount);
const deleteModalMessage = computed(() => {
  switch (deleteModalOption.value) {
    case "delete-applied":
      return t("delete-logo-in-use-prompt");
    case "delete-last":
      return t("delete-last-logo-prompt");
    case "delete-uploaded":
      return t("delete-logo-not-applied-prompt");
  }
});

const authUser = computed(() => authStore.authUser);
const authUserRole = computed(() => authUser.value?.role);
const authUserLevel = computed(() => authUser.value?.level);

const isDeleteModalVisible = ref<boolean>(false);
const deleteModalOption = ref<DeleteModalOption>("delete-uploaded");
const pendingLogoRemovalData = ref<{
  fileName: string;
  mode: "light" | "dark";
} | null>(null);

const isLoading = ref<boolean>(false);
const hasMounted = ref(false);

const emit = defineEmits<{
  (event: "logo-removed", value: "light" | "dark"): void;
  (event: "uploaded-logos-loaded"): void;
  (
    event: "logo-to-submit",
    value: { logoUrl: string; mode: "light" | "dark" },
  ): void;
}>();

const lightLogo = ref<LogoData>({
  applied: null,
  uploaded: null,
  display: null,
  isUploadedUsed: false,
});

const darkLogo = ref<LogoData>({
  applied: null,
  uploaded: null,
  display: null,
  isUploadedUsed: false,
});

async function fetchImageDetails(
  url: string | null | undefined,
): Promise<ImageDetails | null> {
  if (url) {
    try {
      const fullImageUrl = appendFullImageUrl(url);

      const response = await fetch(fullImageUrl);

      const blob = await response.blob();
      const base64 = await blobToBase64(blob);

      return {
        name: extractFilename(fullImageUrl),
        size: blob.size,
        type: blob.type,
        src: base64,
        url,
      };
    } catch (e) {
      /* eslint-disable no-console */
      console.error(e);
      return null;
    }
  } else {
    return null;
  }
}

async function loadLogoData(
  mode: "dark" | "light",
  urls: {
    applied: string | null | undefined;
    uploaded: string | null | undefined;
  },
) {
  let uploaded: ImageDetails | null = null;

  if (urls.uploaded) {
    uploaded = await fetchImageDetails(urls.uploaded);
  }

  const applied = await fetchImageDetails(urls.applied);

  const isUploadedAvailable = !!(uploaded && uploaded.src);

  const logoData: LogoData = {
    applied,
    uploaded,
    display: isUploadedAvailable ? uploaded : applied,
    isUploadedUsed: isUploadedAvailable,
  };

  if (mode === "light") {
    lightLogo.value = logoData;
  } else {
    darkLogo.value = logoData;
  }
}

const handleLogoUpload = async ({
  file,
  mode,
}: {
  file: File;
  mode: "light" | "dark";
}) => {
  try {
    const payload = {
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
      ...(mode === "light" ? { logo: file } : { logo_dark: file }),
    };
    await brandingStore.uploadLogo(payload);

    if (mode === "light") {
      const lightUploaded = uploadedLogos.value.find(
        (logo: string) =>
          logo?.startsWith("assets/branding/") && logo.includes("/light_"),
      );
      const lightLogoUrls = {
        applied: branding.value.logo,
        uploaded: lightUploaded,
      };
      await loadLogoData("light", lightLogoUrls);
    } else {
      const darkUploaded = uploadedLogos.value.find(
        (logo: string) =>
          logo?.startsWith("assets/branding/") && logo.includes("/dark_"),
      );
      const darkLogoUrls = {
        applied: branding.value.logo_dark,
        uploaded: darkUploaded,
      };
      await loadLogoData("dark", darkLogoUrls);
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};

const handleLogoRemoval = (response: {
  fileName: string;
  mode: "light" | "dark";
}) => {
  isDeleteModalVisible.value = true;
  pendingLogoRemovalData.value = response;

  if (response.mode === "light") {
    if (
      lightLogo.value.applied !== null &&
      lightLogo.value.applied.name !== DEFAULT_BRANDING_LIGHT_LOGO_NAME
    ) {
      deleteModalOption.value = "delete-applied";
    } else if (lightLogo.value.uploaded !== null) {
      deleteModalOption.value = "delete-uploaded";
    }
  } else {
    if (darkLogo.value.applied !== null) {
      deleteModalOption.value = "delete-applied";
    } else if (darkLogo.value.uploaded !== null) {
      deleteModalOption.value = "delete-uploaded";
    }
  }

  // Check if only one applied logo exists (eg. removing the last one)
  const hasLightApplied =
    lightLogo.value.applied !== null &&
    lightLogo.value.applied.name !== DEFAULT_BRANDING_LIGHT_LOGO_NAME;
  const hasDarkApplied = darkLogo.value.applied !== null;

  if (
    (hasLightApplied && !hasDarkApplied) ||
    (!hasLightApplied && hasDarkApplied)
  ) {
    deleteModalOption.value = "delete-last";
  }
};

const confirmLogoRemoval = async () => {
  if (!pendingLogoRemovalData.value) {
    return;
  }
  const response = pendingLogoRemovalData.value;
  try {
    isLoading.value = true;

    await brandingStore.removeLogo({
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
      logo: response.fileName,
    });
    if (response.mode === "light") {
      lightLogo.value = {
        applied: null,
        uploaded: null,
        display: null,
        isUploadedUsed: false,
      };
    } else {
      darkLogo.value = {
        applied: null,
        uploaded: null,
        display: null,
        isUploadedUsed: false,
      };
    }
    emit("logo-removed", response.mode);
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  } finally {
    pendingLogoRemovalData.value = null;
    isDeleteModalVisible.value = false;
    isLoading.value = false;
  }
};

const applyLogos = async () => {
  const lightLogoUrls = {
    applied: branding.value.logo,
    uploaded: branding.value.logo,
  };

  const darkLogoUrls = {
    applied: branding.value.logo_dark,
    uploaded: branding.value.logo_dark,
  };

  await loadLogoData("light", lightLogoUrls);
  await loadLogoData("dark", darkLogoUrls);
};

const initLogos = async () => {
  isLoading.value = true;

  const lightUploaded = uploadedLogos.value.find(
    (logo: string) =>
      logo?.startsWith("assets/branding/") && logo.includes("/light_"),
  );

  const darkUploaded = uploadedLogos.value.find(
    (logo: string) =>
      logo?.startsWith("assets/branding/") && logo.includes("/dark_"),
  );

  const lightLogoUrls = {
    applied: branding.value.logo,
    uploaded: lightUploaded,
  };

  const darkLogoUrls = {
    applied: branding.value.logo_dark,
    uploaded: darkUploaded,
  };

  await loadLogoData("light", lightLogoUrls);
  await loadLogoData("dark", darkLogoUrls);
  emit("uploaded-logos-loaded");

  isLoading.value = false;
};

const resetLogos = () => {
  lightLogo.value = {
    applied: null,
    uploaded: null,
    display: null,
    isUploadedUsed: false,
  };

  darkLogo.value = {
    applied: null,
    uploaded: null,
    display: null,
    isUploadedUsed: false,
  };
};

onMounted(() => {
  hasMounted.value = true;
});

watch(
  darkLogo,
  (newLogo) => {
    if (
      hasMounted.value &&
      newLogo &&
      newLogo.isUploadedUsed &&
      newLogo.uploaded &&
      newLogo.uploaded.url !== newLogo.applied?.url
    ) {
      emit("logo-to-submit", {
        logoUrl: newLogo.uploaded.url,
        mode: "dark",
      });
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

watch(
  lightLogo,
  (newLogo) => {
    if (
      hasMounted.value &&
      newLogo &&
      newLogo.isUploadedUsed &&
      newLogo.uploaded &&
      newLogo.uploaded.url !== newLogo.applied?.url
    ) {
      emit("logo-to-submit", {
        logoUrl: newLogo.uploaded.url,
        mode: "light",
      });
    }
  },
  {
    immediate: true,
    deep: true,
  },
);
// I am not happy with this solution, but with this architecture its the only way
// to handle restore to default action from parent
defineExpose({
  resetLogos,
  initLogos,
  applyLogos,
});
</script>
